import React, { createContext, useState, useContext } from 'react';
import { GoogleContactsContext } from './GoogleContactsContext';
import { usePhoneService } from './PhoneServiceContext';

// Create context
const ContactsIntegrationContext = createContext();

export const ContactsIntegrationProvider = ({ children }) => {
  const [addingContact, setAddingContact] = useState(false);
  const [contactResults, setContactResults] = useState({});

  // Get Google Contacts context directly
  const googleContactsContext = useContext(GoogleContactsContext);
  const { 
    createContact: addToGoogleContacts, 
    authorized: googleAuthorized 
  } = googleContactsContext || {
    createContact: () => Promise.reject(new Error('Google Contacts not available')),
    authorized: false
  };

  // Get OpenPhone context
  const { 
    addToOpenPhone, 
    isOpenPhoneConfigured,
    activeService,
    PHONE_SERVICES
  } = usePhoneService();

  /**
   * Add a contact to both Google Contacts and OpenPhone
   * @param {Object} lead - The lead to add as a contact
   * @returns {Promise<Object>} - Result info from both services
   */
  const addContact = async (lead) => {
    try {
      setAddingContact(true);
      const results = {};

      // Log configuration status
      console.log('OpenPhone status:', {
        isConfigured: isOpenPhoneConfigured,
        activeService,
        serviceConstant: PHONE_SERVICES.OPENPHONE
      });

      // Add to Google Contacts first (always do this)
      try {
        if (googleAuthorized) {
          const googleResult = await addToGoogleContacts(lead);
          results.google = googleResult;
        }
      } catch (error) {
        console.error('Error adding to Google Contacts:', error);
        results.googleError = error.message;
      }

      // If OpenPhone is configured and active, add to OpenPhone too
      if (isOpenPhoneConfigured && activeService === 'openPhone') {
        try {
          console.log('Adding contact to OpenPhone:', lead);
          const openPhoneResult = await addToOpenPhone(lead);
          results.openPhone = openPhoneResult;
          console.log('OpenPhone contact creation result:', openPhoneResult);
        } catch (error) {
          console.error('Error adding to OpenPhone:', error);
          results.openPhoneError = error.message;
        }
      } else {
        console.log('Skipping OpenPhone contact creation:', {
          openPhoneConfigured: isOpenPhoneConfigured,
          currentService: activeService,
          shouldBeOpenPhone: 'openPhone'
        });
      }

      // Store results
      setContactResults(prev => ({
        ...prev,
        [lead.id]: results
      }));

      // Show appropriate feedback
      showFeedback(lead, results);

      return results;
    } catch (error) {
      console.error('Error in addContact:', error);
      showErrorFeedback(error.message);
      throw error;
    } finally {
      setAddingContact(false);
    }
  };

  // Helper to show feedback based on results
  const showFeedback = (lead, results) => {
    const feedbackEl = document.getElementById('feedback-message');
    if (!feedbackEl) return;

    let message = '';
    let cssClass = '';

    // Extract names for the message
    const firstName = lead.first_name || '';
    const lastName = lead.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();

    // Determine message based on results
    if (results.googleError && results.openPhoneError) {
      if (results.openPhoneError.includes('Rate limit')) {
        message = `Added to Google Contacts only. OpenPhone rate limit reached - please try again in a minute.`;
        cssClass = 'fixed bottom-4 right-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded shadow-md';
      } else {
        message = `Failed to add contact: ${results.googleError}`;
        cssClass = 'fixed bottom-4 right-4 bg-red-100 text-red-800 px-4 py-2 rounded shadow-md';
      }
    } else if (results.googleError) {
      message = `Added to OpenPhone only: ${fullName}`;
      cssClass = 'fixed bottom-4 right-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded shadow-md';
    } else if (results.openPhoneError) {
      if (results.openPhoneError.includes('Rate limit')) {
        message = `Added to Google Contacts only. OpenPhone rate limit reached - please try again in a minute.`;
      } else {
        message = `Added to Google Contacts only: ${fullName}`;
      }
      cssClass = 'fixed bottom-4 right-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded shadow-md';
    } else if (results.google?.existingContact && results.openPhone?.existingContact) {
      message = `Contact already exists in both services: ${fullName}`;
      cssClass = 'fixed bottom-4 right-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded shadow-md';
    } else if (results.openPhone) {
      message = `Contact added to both services. OpenPhone contacts may take time to appear - try searching for "${fullName}" or "${lead.phone}" in OpenPhone.`;
      cssClass = 'fixed bottom-4 right-4 bg-green-100 text-green-800 px-4 py-2 rounded shadow-md';
    } else {
      message = `Contact added: ${fullName}`;
      cssClass = 'fixed bottom-4 right-4 bg-green-100 text-green-800 px-4 py-2 rounded shadow-md';
    }

    // Show feedback
    feedbackEl.textContent = message;
    feedbackEl.className = cssClass;
    
    // Hide after delay - increase delay for more time to read
    setTimeout(() => {
      feedbackEl.textContent = '';
      feedbackEl.className = 'hidden';
    }, 8000);
  };

  // Helper to show error feedback
  const showErrorFeedback = (message) => {
    const feedbackEl = document.getElementById('feedback-message');
    if (!feedbackEl) return;

    feedbackEl.textContent = `Error: ${message}`;
    feedbackEl.className = 'fixed bottom-4 right-4 bg-red-100 text-red-800 px-4 py-2 rounded shadow-md';
    
    setTimeout(() => {
      feedbackEl.textContent = '';
      feedbackEl.className = 'hidden';
    }, 3000);
  };

  const value = {
    addContact,
    addingContact,
    contactResults
  };

  return (
    <ContactsIntegrationContext.Provider value={value}>
      {children}
    </ContactsIntegrationContext.Provider>
  );
};

// Custom hook to use the contacts integration context
export const useContactsIntegration = () => {
  const context = useContext(ContactsIntegrationContext);
  if (context === undefined) {
    throw new Error('useContactsIntegration must be used within a ContactsIntegrationProvider');
  }
  return context;
};

export default ContactsIntegrationContext; 